<template>
  <div
    ref="item"
    role="listitem"
    variant="tertiary"
    class="HotelSectionsNavigationItem">
    <ai-link
      variant="tertiary"
      :url="href"
      :undecorated="true"
      @click.capture="smoothScroll">
      <ai-typo
        as="span"
        variant="icon-text"
        class="HotelSectionsNavigationItem-Label"
        :class="{ 'HotelSectionsNavigationItem-Label--inverse': inverse }">
        {{ label }}
      </ai-typo>

      <div
        :class="{
          'HotelSectionsNavigationItem-Selected': active,
          'HotelSectionsNavigationItem-NotSelected': !active,
        }" />
    </ai-link>
  </div>
</template>

<script setup lang="ts">
type Props = {
  label: string;
  href: string;
  active: boolean;
  inverse?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  inverse: false,
});

const smoothScroll = (event: MouseEvent) => {
  if (!process.client) {
    return;
  }

  event.preventDefault();

  const scrollPos = window.scrollY || document.documentElement.scrollTop;

  let headerHeight = 0;
  const headerBox = document
    .getElementsByClassName('DefaultLayout-header')
    ?.item(0)
    ?.getBoundingClientRect();
  headerHeight = headerBox?.height ?? headerHeight;

  const bookingBarElement = document
    .getElementsByClassName('HotelBookingBar')
    ?.item(0);

  const element: HTMLElement | null = document.querySelector(props.href);
  if (!element) {
    return;
  }

  let scrollToTop = element.offsetTop;
  if (scrollPos > element.offsetTop) {
    scrollToTop -= headerHeight;
  } else if (
    bookingBarElement &&
    !bookingBarElement.classList.contains('Hotel-bookingBar--sticky')
  ) {
    scrollToTop -= bookingBarElement.getBoundingClientRect().height;
  }

  window.scrollTo({
    top: scrollToTop,
    behavior: 'smooth',
  });
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/functions' as func;

.HotelSectionsNavigationItem button {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  cursor: pointer;

  @include mixins.rem-fallback(min-width, 64);

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(min-width, 96);
  }
}

.HotelSectionsNavigationItem-Label {
  color: colors.$stratos-900;
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  letter-spacing: 0.02em;
  @include mixins.rem-fallback(font-size, 14);
  @include mixins.rem-fallback(line-height, 22);

  &--inverse {
    color: colors.$white;
  }
}

.HotelSectionsNavigationItem-Selected {
  position: relative;
  bottom: func.calcRem(-16);
  width: 100%;
  outline: 2px solid colors.$gold-800;
}

.HotelSectionsNavigationItem-NotSelected {
  position: relative;
  bottom: func.calcRem(-16);
  width: 100%;
}
</style>
